/**
 * NAVI — the travel-agent design system's brand (Figma "Design system - travel
 * agent"; same values as app-frontend's `branding/tenants/navi/tokens.json`).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lists only what Navi changes from `../zenvoya/brand.js`. DELETE THIS FOLDER
 * and both portals go back to Zenvoya — nothing outside it names Navi.
 */
import { ReactComponent as Wordmark } from './wordmark.svg';
import logomark from './logomark.png';
import favicon from './favicon.png';
import { palette } from '../../palette';

/**
 * NAVI PRIMARY — the one value to change to re-colour the Navi brand.
 * Navi's counterpart of Zenvoya's `#6C00F6`: buttons, links, the selected
 * sidebar item and the accents on the (white) panels, and the translucent
 * `primaryAlpha` shades below.
 */
const NAVI_PRIMARY = '#E5102E';

/** `#RRGGBB` + opacity in percent → `rgba(r, g, b, a)`. */
const hexAlpha = (hex, pct) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${pct / 100})`;
};

const navi = {
  primary: NAVI_PRIMARY,
  primaryLight: '#F0667E',
  primaryDark: '#A30025',
  primaryDarker: '#6E0019',
  secondary: '#FBE5EA',
  secondaryLight: '#FFF5F7',
  secondaryMedium: '#F7D6DD',
  secondaryDeep: '#FDF0F3',
};
/** Neutral ink on the white panels. */
const ink = {
  heading: '#1E1E1E',
  body: '#3F3F46',
  muted: '#5F5F66',
  faint: '#8A8A92',
  line: '#ECECEF',
};
const primaryAlpha = (pct) => hexAlpha(NAVI_PRIMARY, pct);
const { white, alpha } = palette;

const naviBrand = {
  id: 'navi',
  name: 'Navi',
  Wordmark,
  logomark,
  favicon,
  // Navi's wordmark is narrower than Zenvoya's (3.2:1 vs 5.3:1): 48px tall here
  // matches the 150px-wide wordmark on the partner login.
  adminLoginLogoHeight: 48,

  colors: {
    brand: {
      primary: navi.primary,
      primaryLight: navi.primaryLight,
      primaryDark: navi.primaryDark,
      secondary: navi.secondary,
      secondaryLight: navi.secondaryLight,
      secondaryDark: navi.secondaryMedium,
    },

    accent: {
      main: navi.primary,
      tint: navi.secondaryLight,
      fill: navi.secondaryDeep,
      border: navi.secondaryMedium,
      borderHover: navi.primaryLight,
      chipText: navi.primaryDark,
      fillAlt: navi.secondary,
      iconSoft: navi.secondaryMedium,
      surface: primaryAlpha(6),
      surfaceBorder: primaryAlpha(18),
      dropHover: navi.secondaryLight,
      dropActive: navi.secondaryDeep,
    },

    servicingAccent: {
      main: navi.primary,
      light: navi.secondaryLight,
      tint: primaryAlpha(4),
      chipBg: navi.secondaryDeep,
      chipFg: navi.primary,
      text: navi.primaryDark,
    },

    sidebarChrome: { icon: ink.faint, iconHover: navi.primary },

    // Sample 3B "white with red": white panels, dark text, red only on the
    // selected item, accents and buttons.
    sidebar: {
      background: white,
      ring: white,
      text: ink.body,
      textHover: navi.primary,
      textActive: navi.primary,
      iconActive: navi.primary,
      accent: navi.primary,
      activeBg: navi.secondaryDeep,
      hoverBg: navi.secondaryLight,
      divider: ink.line,
      scrollThumb: alpha.black[12],
      scrollThumbHover: alpha.black[20],
      groupLabel: ink.faint,
      footerText: ink.faint,
      actionHover: navi.primary,
      brandText: ink.heading,
      edge: `1px solid ${ink.line}`,
    },

    adminLogin: {
      border: navi.secondaryMedium,
      muted: ink.muted,
      primary: navi.primary,
      primaryHover: navi.primaryDark,
      highlight: navi.primary,
      panelBg: navi.secondaryLight,
      panelText: ink.muted,
      panelHeading: ink.heading,
      // The line between panel and form takes the panel colour, so it disappears.
      divider: navi.secondaryLight,
      cardText: ink.heading,
      cardBg: white,
      orb1: primaryAlpha(6),
      orb2: primaryAlpha(4),
      orb3: primaryAlpha(3),
      cardBorder: ink.line,
      cardHoverBg: white,
      cardHoverBorder: navi.secondaryMedium,
      iconBoxBg: navi.secondaryDeep,
      iconBoxBorder: navi.secondaryMedium,
      badgeBorder: ink.line,
      focusRing: primaryAlpha(8),
      ghostHover: primaryAlpha(6),
      buttonShadow: primaryAlpha(30),
      buttonShadowHover: primaryAlpha(40),
      noteBg: primaryAlpha(3),
    },

    // #RRGGBB only where LoginPage appends a hex-alpha suffix (orbs, softAccent).
    partnerLogin: {
      panelBg: navi.secondaryLight,
      panelAccent: navi.primaryDark,
      panelAccentDeep: navi.secondaryDeep,
      accentHighlight: navi.primary,
      accentHighlightSoft: navi.secondaryLight,
      accentHighlightMuted: navi.secondaryMedium,
      softAccent: navi.primary,
      orbColor1: navi.primary,
      orbColor2: navi.primaryLight,
      orbColor3: navi.secondary,
      btnBg: navi.primary,
      btnHoverBg: navi.primaryDark,
      inputFocusBorder: navi.primary,
      inputHoverBorder: navi.primaryLight,
      headingColor: ink.heading,
      tagColor: navi.primary,
      dividerColor: navi.secondaryLight,
      panelInk: {
        heroText: ink.heading,
        panelText: ink.muted,
        stepTitle: ink.heading,
        stepText: ink.muted,
        stepIcon: navi.primary,
        footerText: ink.faint,
        poweredText: ink.muted,
      },
    },

    partnerConsolePrimary: navi.primary,

    partnerConsoleAlpha: { 8: primaryAlpha(8), 20: primaryAlpha(20) },

    pill: {
      bg: navi.secondaryDeep,
      text: navi.primaryDark,
      icon: navi.primary,
      divider: navi.primaryLight,
      tagBg: navi.secondary,
      borderHover: navi.secondaryMedium,
      ring: navi.secondaryMedium,
      borderFocus: navi.primary,
    },

    partnerFallback: {
      primary: navi.primary,
      primaryLight: navi.secondary,
      secondary: navi.secondary,
    },

    partnerDefaultBranding: {
      primaryColor: navi.primary,
      secondaryColor: navi.secondary,
      accentColor: navi.primaryDark,
    },
  },
};

export default naviBrand;
